import { APIURL, apiGet, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, toggleWidgetTheme, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';
import stockHighlightsSectionStyleText from './../../assets/css/showcase/stockHighlightsSection.css?raw';

export class TraderverseStockHighlightsSection {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: Record<string, any> | null = null;
  private widgetResizeObserver: ResizeObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' },
      { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' },
      { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' },
      { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' },
      { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' },
      { label: 'COST', value: 'COST' },
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '420px',
      autosize: true,
      hideSymbol: true,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()} ${this.config.title || 'Highlights Section'}</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                  ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
              </div>`}
            <span></span>
          </div>
          <div class="chartDv" id="stockHighlightsContainer">
            <div class="tv-empty-state">Loading highlights...</div>
          </div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  private onSelectChange(stock: any): void {
    this.changeSymbol(stock.symbol, stock);
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (nextStockId: string) => this.setupChart(nextStockId),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }

    const localStyle = document.createElement('style');
    localStyle.textContent = stockHighlightsSectionStyleText;
    this.shadowRoot.appendChild(localStyle);
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const response = await apiGet({
        method: 'POST',
        url: `${APIURL}/v1/overview/highlights`,
        body: { symbol },
      });

      if (!response.ok) throw new Error('Failed to fetch data');
      const payload = await response.json();
      this.currentData = Array.isArray(payload?.data) ? payload.data[0] : payload?.data ?? payload;

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      this.currentData = null;
      this.renderErrorState();
      this.config.onError?.(err as Error);
      this.emit('error', err);
    }
  }

  private updateDisplay(): void {
    if (!this.currentData) {
      this.renderEmptyState('No highlights data available.');
      return;
    }
    this.renderHighlights(this.currentData);
  }

  private renderEmptyState(message: string): void {
    const container = this.shadowRoot.querySelector('#stockHighlightsContainer');
    if (!container) return;
    container.innerHTML = getNoDataHTML(message.replace(/\.$/, ''));
  }

  private renderErrorState(): void {
    const container = this.shadowRoot.querySelector('#stockHighlightsContainer');
    if (!container) return;
    container.innerHTML = `<div class="tv-error-state">Unable to load highlights right now.</div>`;
  }

  private renderHighlights(data: Record<string, any>): void {
    const container = this.shadowRoot.querySelector('#stockHighlightsContainer');
    if (!container) return;

    const sentiment = this.getSentimentInfo(data);
    const dayRange = this.getRangeValues(data, [
      ['day_low', 'low_24h', 'low', 'low_price'],
      ['day_high', 'high_24h', 'high', 'high_price'],
      ['market_range', 'range_24h'],
    ]);
    const weekRange = this.getRangeValues(data, [
      ['week_low', 'seven_day_low', 'low_7d'],
      ['week_high', 'seven_day_high', 'high_7d'],
      ['week_range', 'seven_day_range', 'range_7d'],
    ]);
    const openPrice = this.getFirstValue(data, ['open', 'open_price', 'price_open']);
    const openPosition = this.getPositionFromValues(openPrice, dayRange.lowRaw, dayRange.highRaw, 0.04);

    const rows = [
      {
        label: '7d Low/7d High',
        value: `${this.formatMetricValue(weekRange.lowRaw)} / ${this.formatMetricValue(weekRange.highRaw)}`,
        colorClass: 'value-green',
      },
      {
        label: 'Market Cap Dominance',
        value: this.formatMetricValue(this.getFirstValue(data, ['market_cap_dominance', 'dominance'])),
        colorClass: '',
      },
      {
        label: 'Trading Volume',
        value: this.formatMetricValue(this.getFirstValue(data, ['trading_volume', 'total_volume', 'volume']), 'compact'),
        colorClass: 'value-blue',
      },
      {
        label: 'All Time High',
        value: this.formatMetricValue(this.getFirstValue(data, ['all_time_high', 'ath', 'record_high'])),
        colorClass: 'value-blue',
      },
      {
        label: 'All Time Low',
        value: this.formatMetricValue(this.getFirstValue(data, ['all_time_low', 'atl', 'record_low'])),
        colorClass: '',
      },
    ];

    container.innerHTML = `
      <div class="stockHighlightsPanel">
        <div class="tv-summary">
          <h5 class="tv-summary-title">Ratings</h5>
          <div class="tv-sentiment">
            ${this.getSentimentIcon()}
            <span>${sentiment.label}</span>
          </div>
        </div>
        <div class="tv-card">

          <div>
            <div class="tv-meter-labels">
              <span>Bullish</span>
              <span>Bearish</span>
            </div>
            <div class="tv-meter-track">
              <span class="tv-meter-marker" style="left:${(sentiment.position * 100).toFixed(2)}%;"></span>
            </div>
            <div class="tv-range-values">
              <div class="tv-range-low">
                <span class="tv-caption">Low</span>
                ${this.formatMetricValue(dayRange.lowRaw)}
              </div>
              <div class="tv-range-mid">
                <span class="tv-caption">24h Range</span>
              </div>
              <div class="tv-range-high">
                <span class="tv-caption">High</span>
                ${this.formatMetricValue(dayRange.highRaw)}
              </div>
            </div>
          </div>

          <div class="tv-open-block">
            <div class="tv-open-track">
              <span class="tv-open-marker" style="left:${(openPosition * 100).toFixed(2)}%;"></span>
            </div>
            <div class="tv-open-values">
              <div>Open</div>
              <div class="tv-open-value">${this.formatMetricValue(openPrice)}</div>
            </div>
          </div>

          <div class="eventsMain shareStructure tv-stat-list">
            ${rows.map((row) => `
              <div class="item body">
                <p>${row.label}</p>
                <p class="${row.colorClass}">${row.value}</p>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `;
  }

  private getSentimentInfo(data: Record<string, any>): { label: string; position: number } {
    const raw = this.getFirstValue(data, ['sentiment', 'rating', 'signal', 'outlook', 'market_sentiment']);
    const rawLabel = raw == null ? 'Neutral' : String(raw).trim();
    const label = rawLabel ? rawLabel[0].toUpperCase() + rawLabel.slice(1) : 'Neutral';
    const lower = label.toLowerCase();

    const numericScore = Number(this.getFirstValue(data, ['sentiment_score', 'rating_score', 'score']));
    if (!Number.isNaN(numericScore) && Number.isFinite(numericScore)) {
      const clamped = Math.max(0, Math.min(100, numericScore));
      return { label, position: clamped / 100 };
    }
    if (lower.includes('bull') || lower.includes('positive')) return { label, position: 0.22 };
    if (lower.includes('bear') || lower.includes('negative')) return { label, position: 0.8 };
    return { label, position: 0.5 };
  }

  private getSentimentIcon(): string {
    return `
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.8"/>
        <circle cx="9" cy="10" r="1" fill="currentColor"/>
        <circle cx="15" cy="10" r="1" fill="currentColor"/>
        <path d="M8.5 15C9.3 14.2 10.4 13.8 12 13.8C13.6 13.8 14.7 14.2 15.5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
    `;
  }

  private getFirstValue(data: Record<string, any>, keys: string[]): any {
    for (const key of keys) {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') return data[key];
    }
    return null;
  }

  private getRangeValues(
    data: Record<string, any>,
    keySets: [string[], string[], string[]]
  ): { lowRaw: any; highRaw: any } {
    let low = this.getFirstValue(data, keySets[0]);
    let high = this.getFirstValue(data, keySets[1]);

    if ((low == null || high == null) && keySets[2]?.length) {
      const range = this.getFirstValue(data, keySets[2]);
      const parsed = this.parseRange(range);
      low = low ?? parsed.low;
      high = high ?? parsed.high;
    }

    return { lowRaw: low, highRaw: high };
  }

  private parseRange(value: any): { low: number | null; high: number | null } {
    if (typeof value !== 'string') return { low: null, high: null };
    const matches = value.match(/\d[\d,]*(?:\.\d+)?/g) || [];
    if (matches.length < 2) return { low: null, high: null };
    const lowValue = matches[0];
    const highValue = matches[1];
    if (!lowValue || !highValue) return { low: null, high: null };
    return {
      low: Number(lowValue.replace(/,/g, '')),
      high: Number(highValue.replace(/,/g, '')),
    };
  }

  private getPositionFromValues(value: any, min: any, max: any, fallback: number): number {
    const current = this.toNumber(value);
    const low = this.toNumber(min);
    const high = this.toNumber(max);

    if (current == null || low == null || high == null || high <= low) return fallback;
    return Math.max(0.03, Math.min(0.97, (current - low) / (high - low)));
  }

  private toNumber(value: any): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const sanitized = value.replace(/[$,%\s]/g, '').replace(/,/g, '');
      const parsed = Number(sanitized);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private formatMetricValue(value: any, mode: 'default' | 'compact' = 'default'): string {
    if (value == null || value === '') return '-';
    if (typeof value === 'string' && mode === 'default') return value;

    const num = this.toNumber(value);
    if (num == null) return String(value);

    if (mode === 'compact') {
      const abs = Math.abs(num);
      if (abs >= 1_000_000_000_000) return `${(num / 1_000_000_000_000).toFixed(2)}T`;
      if (abs >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
      if (abs >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
      if (abs >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
    }

    if (Math.abs(num) >= 1000) {
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return num.toFixed(2);
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window {
    TraderverseStockHighlightsSection?: typeof TraderverseStockHighlightsSection;
  }
}

if (typeof window !== 'undefined') {
  window.TraderverseStockHighlightsSection = TraderverseStockHighlightsSection;
}
